
import React, { useState, useEffect } from 'react';
import { GoogleAuthProvider, signInWithPopup, auth, firebaseConfig, db } from '../firebase';
import { 
    sanitizeEntityName, 
    sanitizePersonName, 
    validatePhoneNumber, 
    compressImage,
    detectUserCountry,
    getCountryFromPhone,
    getCountryNameFromPhone,
    getDialingCodeForCountry
} from '../lib/helpers';
import { useTranslate } from '../hooks/useTranslate';
import { Role, UserProfile, InvitationCode, OrgType, Organization, Gender } from '../types';
import { ImageUpload, LanguageSelector, PhoneInput, GenderSelect } from './common';
import { COUNTRIES_STATES } from '../lib/constants';
import { collection, query, where, limit, getDocs } from "firebase/firestore";

const EduBridgeLogo = () => (
    <div className="flex flex-col items-center justify-center mb-8">
        <h1 className="text-6xl font-bold text-gray-800 tracking-tighter">EduGravity</h1>
    </div>
);


export const LoginScreen = () => {
  const handleGoogleSignIn = async () => {
    if (firebaseConfig.apiKey.startsWith('REPLACE_WITH')) {
        alert("SETUP INCOMPLETE:\n\nPlease open the 'firebase.ts' file and replace the placeholder 'apiKey' with your actual Firebase Web API key.\n\nYou can find this key in your Firebase project's settings.");
        return;
    }
    
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error: any) {
      if (error?.code === 'auth/popup-closed-by-user' || error?.code === 'auth/cancelled-popup-request') {
        // User closed or dismissed the popup voluntarily
        return;
      }
      console.error("Google Sign-In failed:", error);
      
      let errorMessage = "Could not sign in with Google. Please check your internet connection and try again.";

      if (error.code) {
        switch (error.code) {
          case 'auth/operation-not-allowed':
            errorMessage = "Google Sign-In is not enabled for this project.\n\nACTION REQUIRED:\n1. Go to your Firebase Project Console.\n2. Navigate to Authentication > Sign-in method.\n3. Click 'Add new provider' and enable 'Google'.";
            break;
          case 'auth/unauthorized-domain':
            errorMessage = "This domain is not authorized for authentication.\n\nACTION REQUIRED:\n1. Go to your Firebase Project Console.\n2. Navigate to Authentication > Settings > Authorized domains.\n3. Click 'Add domain' and enter this app's domain:\n" + window.location.hostname;
            break;
          case 'auth/popup-closed-by-user':
            // This is not an error, so we silently return.
            return;
          case 'auth/popup-blocked':
            errorMessage = "The sign-in popup was blocked by your browser. Please allow popups for this site and try again.";
            break;
          default:
            errorMessage = `An unexpected error occurred during sign-in. Please check the console for more details.\n(Code: ${error.code})`;
        }
      }
      
      alert(errorMessage);
    }
  };

  return (
    <div 
        className="h-screen w-full flex items-center justify-center p-4 relative overflow-hidden bg-gray-100"
    >
        {/* Background Image Layer */}
        {/* ACTION REQUIRED: Replace the URL below with your specific image URL */}
        <div 
            className="absolute inset-0 z-0 bg-cover bg-center"
            style={{ 
                backgroundImage: `url('https://images.unsplash.com/photo-1509062522246-3755977927d7?q=80&w=2600&auto=format&fit=crop')`,
                filter: 'brightness(0.8)' // Slightly darken background to make the card pop
            }}
        />
        
        {/* Content Card - Glassmorphism Effect */}
        <div className="relative z-10 w-full max-w-md bg-white/90 backdrop-blur-md p-8 rounded-3xl shadow-2xl border border-white/50 flex flex-col items-center text-center">
            <EduBridgeLogo />
            <button
                onClick={handleGoogleSignIn}
                className="w-full bg-white border-2 border-gray-200 text-gray-700 py-3 rounded-xl font-medium shadow-md flex items-center justify-center gap-3 px-4 hover:bg-gray-50 hover:shadow-lg transition-all"
            >
                <svg className="w-6 h-6" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24s.92 7.54 2.56 10.78l7.97-6.19z"></path>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                <path fill="none" d="M0 0h48v48H0z"></path>
                </svg>
                Sign in with Google
            </button>
        </div>
        
        {/* Footer/Credit */}
        <div className="absolute bottom-4 text-white/80 text-xs z-10">
            Powered by EduGravity
        </div>
    </div>
  );
};


// OnboardingFlow component for new user registration
export const OnboardingFlow = ({ onComplete, onCreateOrg, onUpdateUser, onBack, prefilledData, invitationCodes }: {
    onComplete: (profileData: Partial<UserProfile>, usedCode?: InvitationCode) => Promise<UserProfile>;
    onCreateOrg: (name: string, type: OrgType, address: { street?: string; city: string; state: string; country: string; }, photoUrl: string | undefined, adminId: string, phone?: string, verification?: any) => Promise<Organization>;
    onUpdateUser: (userId: string, data: Partial<UserProfile>) => void;
    onBack: () => void;
    prefilledData: Partial<UserProfile> | null;
    invitationCodes: InvitationCode[]; // Kept for interface compatibility but not used for Teacher validation anymore
}) => {
    // Smart Country & Dialing Code Auto-Detection (Zero permissions, timezone/locale based)
    const initialDetected = detectUserCountry();
    const prefilledPhoneCountry = getCountryFromPhone(prefilledData?.mobileNumber);
    const defaultCountryName = prefilledPhoneCountry?.name || initialDetected.name;
    const defaultDialingCode = prefilledPhoneCountry?.code || initialDetected.code;

    const [selectedDialingCode, setSelectedDialingCode] = useState(defaultDialingCode);
    const [selectedCountryName, setSelectedCountryName] = useState(defaultCountryName);

    const [role, setRole] = useState<Role | ''>(prefilledData?.role || '');
    const [step, setStep] = useState(prefilledData?.role === 'principal' ? 3 : 1);
    const [profile, setProfile] = useState<Partial<UserProfile>>({
        name: prefilledData?.name || '',
        email: prefilledData?.email || '',
        photoUrl: prefilledData?.photoUrl,
        googleId: prefilledData?.googleId,
        language: prefilledData?.language || 'en',
        gender: prefilledData?.gender || undefined,
        mobileNumber: prefilledData?.mobileNumber || ''
    });
    // Org details including phone and verification (defaults to detected/selected country)
    const [orgDetails, setOrgDetails] = useState({ 
        name: '', 
        type: 'school' as OrgType, 
        address: { street: '', city: '', state: '', country: defaultCountryName }, 
        photoUrl: '', 
        phone: '',
        verification: {
            affiliationNumber: '',
            publicUrl: '',
            documentUrl: '',
            documentName: '',
            officialEmail: ''
        }
    });
    const [invitationCode, setInvitationCode] = useState('');
    const [error, setError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [availableStates, setAvailableStates] = useState<string[]>([]);
    const [invitedOrg, setInvitedOrg] = useState<{ id: string; name: string; photoUrl?: string } | null>(null);

    const t = useTranslate(profile.language || 'en');
    
    // Auto-detect Vice Principal / Co-Admin invitation by email
    useEffect(() => {
        const checkVicePrincipalInvite = async () => {
            const userEmail = (prefilledData?.email || profile.email || '').toLowerCase().trim();
            if (!userEmail) return;

            try {
                const q = query(
                    collection(db, 'organizations'),
                    where('vicePrincipalEmails', 'array-contains', userEmail),
                    limit(1)
                );
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    const docSnap = querySnapshot.docs[0];
                    const data = docSnap.data();
                    setInvitedOrg({
                        id: docSnap.id,
                        name: data.name,
                        photoUrl: data.photoUrl
                    });
                    setRole('principal');
                    setStep(2);
                }
            } catch (err) {
                console.warn("Error checking vice principal invitation:", err);
            }
        };

        checkVicePrincipalInvite();
    }, [prefilledData?.email, profile.email]);

    useEffect(() => {
        const countryData = COUNTRIES_STATES.find(c => c.name === orgDetails.address.country);
        setAvailableStates(countryData ? countryData.states : []);
    }, [orgDetails.address.country]);

    const handleDocUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setError('File size must be under 5MB.');
                return;
            }
            if (file.type.startsWith('image/')) {
                try {
                    const compressed = await compressImage(file, 600, 0.8);
                    setOrgDetails(prev => ({
                        ...prev,
                        verification: {
                            ...prev.verification,
                            documentUrl: compressed,
                            documentName: file.name
                        }
                    }));
                    return;
                } catch (err) {
                    console.warn("Doc image compression skipped:", err);
                }
            }
            const reader = new FileReader();
            reader.onload = (ev) => {
                setOrgDetails(prev => ({
                    ...prev,
                    verification: {
                        ...prev.verification,
                        documentUrl: ev.target?.result as string,
                        documentName: file.name
                    }
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleNext = async () => {
        setError('');
        if (step === 1) {
            if (role) setStep(2);
            return;
        }
        
        if (step === 2) {
            if (!profile.name?.trim()) { setError("Full Name is required."); return; }
            if (!profile.photoUrl) { setError("Profile photo is required."); return; }
            if (!profile.gender) { setError("Gender is required."); return; }
            if (!profile.mobileNumber) { setError("Phone Number is required."); return; }
            if (!validatePhoneNumber(profile.mobileNumber)) { setError("Invalid Phone Number for the selected country."); return; }

            // Principal moves to step 3 synchronously (or joins as VP immediately if invited)
            if (role === 'principal') {
                if (invitedOrg) {
                    setIsProcessing(true);
                    try {
                        const finalProfile: Partial<UserProfile> = {
                            ...profile,
                            role: 'principal',
                            orgId: invitedOrg.id,
                            approvalStatus: 'approved',
                            isVicePrincipal: true
                        };
                        await onComplete(finalProfile);
                    } catch (e: any) {
                        console.error("Vice Principal onboarding failed:", e);
                        setError(`Failed to join ${invitedOrg.name}: ${e.message || "Unknown error"}`);
                        setIsProcessing(false);
                    }
                    return;
                }

                const phoneCountry = getCountryFromPhone(profile.mobileNumber);
                const activeCountry = phoneCountry?.name || selectedCountryName || defaultCountryName;
                const activeDialingCode = phoneCountry?.code || selectedDialingCode || defaultDialingCode;

                setOrgDetails(prev => ({
                    ...prev,
                    address: {
                        ...prev.address,
                        country: prev.address.country || activeCountry,
                    }
                }));
                setStep(3);
                return;
            }

            // Teacher and Parent submissions are async
            setIsProcessing(true);
            try {
                if (role === 'teacher') {
                    if (!invitationCode) {
                        setError("Invitation code is required.");
                        setIsProcessing(false);
                        return;
                    }
                    
                    const codeQuery = query(
                        collection(db, "invitationCodes"), 
                        where("code", "==", invitationCode), 
                        where("type", "==", "teacher"),
                        where("isUsed", "==", false),
                        limit(1)
                    );
                    const querySnapshot = await getDocs(codeQuery);

                    if (querySnapshot.empty) {
                        setError(t('code_not_found'));
                        setIsProcessing(false);
                        return;
                    }
                    
                    const codeDoc = querySnapshot.docs[0];
                    const code = { id: codeDoc.id, ...codeDoc.data() } as InvitationCode;

                    const finalProfile: Partial<UserProfile> = {
                        ...profile,
                        role,
                        orgId: code.orgId,
                        approvalStatus: 'pending',
                        invitationCodeUsed: code.code,
                        invitationCodeId: code.id,
                    };
                    await onComplete(finalProfile, code);
                } else if (role === 'parent') {
                    const finalProfile: Partial<UserProfile> = {
                        ...profile,
                        role,
                        approvalStatus: 'approved',
                    };
                    await onComplete(finalProfile);
                }
            } catch (e: any) {
                console.error("Onboarding submission failed:", e);
                // Handle permission errors gracefully
                if (e.code === 'permission-denied') {
                     setError("Unable to validate code. Please check your internet connection.");
                } else {
                     setError("An error occurred. Please try again.");
                }
                setIsProcessing(false);
            }
        }
    };

    const handleFinishPrincipalSetup = async () => {
        // Validation for Org
        if (!orgDetails.name || !orgDetails.address.country || !orgDetails.address.state || !orgDetails.address.city || !orgDetails.address.street) {
            setError("All organization address fields are required.");
            return;
        }
        if (!orgDetails.photoUrl) {
            setError("Organization logo is required.");
            return;
        }
        if (!orgDetails.phone) {
            setError("Organization phone number is required.");
            return;
        }
        if (!validatePhoneNumber(orgDetails.phone)) {
            setError("Invalid Organization Phone Number for the selected country.");
            return;
        }

        setIsProcessing(true);
        setError('');
        try {
            console.log("Starting Principal Setup with Anti-Spam verification...");
            
            // Step 1: Create or update the user profile with pending status
            const adminProfileData: Partial<UserProfile> = {
                ...profile,
                name: profile.name?.trim() || prefilledData?.name || 'School Administrator',
                role: 'principal',
                approvalStatus: 'pending',
            };
            const newUser = await onComplete(adminProfileData);

            // Step 2: Create the organization with pending status & verification payload
            const newOrg = await onCreateOrg(
                orgDetails.name, 
                orgDetails.type, 
                orgDetails.address, 
                orgDetails.photoUrl, 
                newUser.id,
                orgDetails.phone,
                orgDetails.verification
            );
            
            await onUpdateUser(newUser.id, { orgId: newOrg.id });

        } catch (e: any) {
            console.error("Principal setup failed:", e);
            setError(`Setup failed: ${e.message || "Unknown error"}. Check console for details.`);
        } finally {
            setIsProcessing(false);
        }
    };

    const RoleButton = ({ icon, title, description, selected, onClick }: { icon: string, title: string, description: string, selected: boolean, onClick: () => void }) => (
        <button
            onClick={onClick}
            className={`w-full p-6 border-2 rounded-2xl text-left flex items-start gap-4 transition-colors ${selected ? 'bg-indigo-50 border-indigo-500' : 'bg-white border-gray-200 hover:border-gray-400'}`}
        >
            <span className={`material-icons-round text-3xl p-3 rounded-full ${selected ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-500'}`}>{icon}</span>
            <div>
                <h3 className="font-bold text-lg text-gray-800">{title}</h3>
                <p className="text-gray-500">{description}</p>
            </div>
        </button>
    );

    const getStep2ButtonText = () => {
        if (invitedOrg) return `Join ${invitedOrg.name} as Vice Principal`;
        switch (role) {
            case 'principal': return t('next_setup_org');
            case 'parent': return t('finish_setup');
            case 'teacher': return t('request_to_join');
            default: return 'Next';
        }
    };

    return (
        <div className="h-screen w-full max-w-md mx-auto p-4 flex flex-col bg-gray-50">
            <div className="flex items-center justify-between mb-8">
                <button onClick={step === 1 ? onBack : () => setStep(s => s - 1)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full">
                    <span className="material-icons-round">arrow_back</span>
                </button>
                <h1 className="text-2xl font-bold text-gray-800">EduGravity</h1>
            </div>

            <div className="flex-1 overflow-y-auto">
                {step === 1 && (
                    <div className="p-4 space-y-4">
                        {invitedOrg && (
                            <div className="bg-indigo-50 border-2 border-indigo-200 rounded-2xl p-4 flex items-center justify-between gap-3 shadow-xs">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-lg">
                                        <span className="material-icons-round text-xl">supervisor_account</span>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">Vice Principal Invitation</p>
                                        <h4 className="font-bold text-gray-900 text-sm">{invitedOrg.name}</h4>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => { setRole('principal'); setStep(2); }}
                                    className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer"
                                >
                                    Accept
                                </button>
                            </div>
                        )}
                        <h2 className="text-3xl font-bold text-center mb-6">{t('who_are_you')}</h2>
                        <RoleButton icon="corporate_fare" title={t('principal_admin')} description={t('principal_admin_desc')} selected={role === 'principal'} onClick={() => setRole('principal')} />
                        <RoleButton icon="school" title={t('teacher')} description={t('teacher_desc')} selected={role === 'teacher'} onClick={() => setRole('teacher')} />
                        <RoleButton icon="escalator_warning" title={t('parent')} description={t('parent_desc')} selected={role === 'parent'} onClick={() => setRole('parent')} />
                    </div>
                )}

                {step === 2 && (
                    <div className="p-4 space-y-4">
                         {invitedOrg && (
                             <div className="bg-gradient-to-r from-slate-900 to-indigo-950 text-white rounded-2xl p-4 shadow-md flex items-center gap-3.5">
                                 <div className="w-12 h-12 rounded-xl bg-indigo-500/30 border border-indigo-400/40 text-indigo-200 flex items-center justify-center text-2xl flex-shrink-0">
                                     {invitedOrg.photoUrl ? (
                                         <img src={invitedOrg.photoUrl} alt="" className="w-full h-full object-cover rounded-xl" />
                                     ) : (
                                         <span className="material-icons-round text-2xl">supervisor_account</span>
                                     )}
                                 </div>
                                 <div className="flex-1 min-w-0">
                                     <span className="inline-block text-[10px] font-black uppercase tracking-wider bg-indigo-500/40 text-indigo-200 px-2 py-0.5 rounded-full mb-1 border border-indigo-400/30">
                                         Vice Principal Invitation
                                     </span>
                                     <h3 className="font-bold text-sm truncate">{invitedOrg.name}</h3>
                                     <p className="text-xs text-indigo-200/80">Complete your profile below to enter the administration console.</p>
                                 </div>
                             </div>
                         )}
                         <h2 className="text-3xl font-bold text-center mb-6">{t('profile_details')}</h2>
                         <ImageUpload onImageUpload={url => setProfile(p => ({ ...p, photoUrl: url }))} currentImage={profile.photoUrl} label="Your Photo (Required)" />
                         <input type="text" value={profile.name} onChange={e => setProfile(p => ({ ...p, name: sanitizePersonName(e.target.value) }))} placeholder={t('your_full_name')} maxLength={30} className="w-full p-3 border-2 border-gray-200 rounded-lg" />
                         <GenderSelect 
                            value={profile.gender} 
                            onChange={g => setProfile(p => ({ ...p, gender: g }))} 
                            label="Gender (Required)" 
                            options={['male', 'female']} // Restricted for adults
                         />
                         <PhoneInput 
                            value={profile.mobileNumber || ''} 
                            onChange={num => setProfile(p => ({ ...p, mobileNumber: num }))} 
                            label="Phone Number (Required)" 
                            defaultCountryCode={selectedDialingCode}
                            onCountryCodeChange={(code, countryName) => {
                                setSelectedDialingCode(code);
                                setSelectedCountryName(countryName);
                                // Flow through to organization setup default country
                                setOrgDetails(prev => ({
                                    ...prev,
                                    address: {
                                        ...prev.address,
                                        country: countryName,
                                        state: ''
                                    }
                                }));
                            }}
                         />
                         <LanguageSelector selectedLanguage={profile.language || 'en'} onSelectLanguage={lang => setProfile(p => ({ ...p, language: lang }))} />
                         
                         {role === 'teacher' && (
                             <input type="text" value={invitationCode} onChange={e => setInvitationCode(e.target.value.toUpperCase())} placeholder={t('teacher_invitation_code')} maxLength={6} className="w-full p-3 border-2 border-gray-200 rounded-lg uppercase" />
                         )}
                         {error && <p className="text-red-500 text-sm text-center font-bold bg-red-50 p-2 rounded-lg">{error}</p>}
                    </div>
                )}
                
                {step === 3 && role === 'principal' && (
                    <div className="p-4 space-y-4">
                         <div className="text-center mb-4">
                            <h2 className="text-2xl font-bold text-gray-800">{t('setup_organization')}</h2>
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 mt-2 bg-indigo-50 border border-indigo-100 text-indigo-700 rounded-full text-[11px] font-semibold">
                                <span className="material-icons-round text-xs text-indigo-600">verified_user</span>
                                <span>Anti-Spam &amp; Institution Verification</span>
                            </div>
                         </div>

                         <ImageUpload onImageUpload={url => setOrgDetails(o => ({...o, photoUrl: url}))} currentImage={orgDetails.photoUrl} label="Organization Logo (Required)"/>
                         <input type="text" value={orgDetails.name} onChange={e => setOrgDetails(o => ({...o, name: sanitizeEntityName(e.target.value)}))} placeholder="Organization Name (Required)" maxLength={50} className="w-full p-3 border-2 border-gray-200 rounded-lg text-sm" />
                         
                         <div>
                            <PhoneInput 
                                value={orgDetails.phone} 
                                onChange={val => setOrgDetails(o => ({...o, phone: val}))} 
                                label="Organization Contact Phone (Required)" 
                                defaultCountryCode={getDialingCodeForCountry(orgDetails.address.country) || selectedDialingCode || defaultDialingCode}
                            />
                         </div>
                         
                         <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Country *</label>
                                <select
                                    value={orgDetails.address.country}
                                    onChange={e => {
                                        const newCountry = e.target.value;
                                        setOrgDetails(o => ({ ...o, address: { ...o.address, country: newCountry, state: '' } }));
                                    }}
                                    className="w-full p-2.5 border-2 border-gray-200 rounded-lg text-xs"
                                >
                                    <option value="" disabled>Select Country</option>
                                    {COUNTRIES_STATES.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">State *</label>
                                <select
                                    value={orgDetails.address.state}
                                    onChange={e => setOrgDetails(o => ({ ...o, address: { ...o.address, state: e.target.value } }))}
                                    className="w-full p-2.5 border-2 border-gray-200 rounded-lg text-xs"
                                    disabled={!orgDetails.address.country}
                                >
                                    <option value="" disabled>Select State</option>
                                    {availableStates.map(state => <option key={state} value={state}>{state}</option>)}
                                </select>
                            </div>
                         </div>
                         <input type="text" value={orgDetails.address.city} onChange={e => setOrgDetails(o => ({...o, address: {...o.address, city: e.target.value}}))} placeholder="City (Required)" maxLength={50} className="w-full p-3 border-2 border-gray-200 rounded-lg text-sm" />
                         <input type="text" value={orgDetails.address.street} onChange={e => setOrgDetails(o => ({...o, address: {...o.address, street: e.target.value}}))} placeholder="Street / Area Address (Required)" maxLength={100} className="w-full p-3 border-2 border-gray-200 rounded-lg text-sm" />
                         <select value={orgDetails.type} onChange={e => setOrgDetails(o => ({...o, type: e.target.value as OrgType}))} className="w-full p-3 border-2 border-gray-200 rounded-lg text-sm">
                            <option value="school">K-12 School</option>
                            <option value="coaching">Coaching / Tuition Center</option>
                         </select>

                         {/* Optional Verification Section */}
                         <div className="pt-3 border-t-2 border-gray-100">
                            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                                <span className="material-icons-round text-sm text-indigo-600">shield</span>
                                <span>Verification Proof (Optional)</span>
                            </h4>
                            <p className="text-[11px] text-gray-500 mb-3">
                                Provide any of the following to expedite your one-time school verification:
                            </p>

                            <div className="space-y-3">
                                <div>
                                    <label className="text-[11px] font-semibold text-gray-600 block mb-1">
                                        Board / Trust / Affiliation / Reg No.
                                    </label>
                                    <input 
                                        type="text" 
                                        value={orgDetails.verification.affiliationNumber} 
                                        onChange={e => setOrgDetails(o => ({ ...o, verification: { ...o.verification, affiliationNumber: e.target.value } }))} 
                                        placeholder="e.g. CBSE/2024/9876 or Reg ID" 
                                        maxLength={40} 
                                        className="w-full p-2.5 border-2 border-gray-200 rounded-lg text-xs font-mono" 
                                    />
                                </div>

                                <div>
                                    <label className="text-[11px] font-semibold text-gray-600 block mb-1">
                                        Website / Google Maps / Facebook Page Link
                                    </label>
                                    <input 
                                        type="text" 
                                        value={orgDetails.verification.publicUrl} 
                                        onChange={e => setOrgDetails(o => ({ ...o, verification: { ...o.verification, publicUrl: e.target.value } }))} 
                                        placeholder="https://maps.app.goo.gl/... or website" 
                                        maxLength={150} 
                                        className="w-full p-2.5 border-2 border-gray-200 rounded-lg text-xs" 
                                    />
                                </div>

                                <div>
                                    <label className="text-[11px] font-semibold text-gray-600 block mb-1">
                                        School Letterhead / ID / Certificate Photo
                                    </label>
                                    <label className="flex items-center justify-center gap-2 p-2.5 border-2 border-dashed border-gray-300 hover:border-indigo-500 rounded-lg cursor-pointer bg-white hover:bg-indigo-50/50 transition-colors">
                                        <span className="material-icons-round text-indigo-600 text-base">upload_file</span>
                                        <span className="text-xs font-medium text-gray-600 truncate">
                                            {orgDetails.verification.documentName || 'Attach Photo or PDF'}
                                        </span>
                                        <input type="file" accept="image/*,application/pdf" onChange={handleDocUpload} className="hidden" />
                                    </label>
                                </div>
                            </div>
                         </div>

                         {error && <p className="text-red-500 text-sm text-center font-bold bg-red-50 p-2 rounded-lg">{error}</p>}
                    </div>
                )}
            </div>

            <div className="p-4">
                {step < 3 && (
                    <button onClick={handleNext} disabled={(step === 1 && !role) || isProcessing} className="w-full py-4 bg-indigo-600 text-white font-bold rounded-xl disabled:opacity-50 flex items-center justify-center">
                        {isProcessing ? <span className="material-icons-round animate-spin">sync</span> : (step === 1 ? "Next" : getStep2ButtonText())}
                    </button>
                )}
                {step === 3 && (
                    <button onClick={handleFinishPrincipalSetup} disabled={isProcessing} className="w-full py-4 bg-green-600 text-white font-bold rounded-xl disabled:opacity-50 flex items-center justify-center">
                        {isProcessing ? <span className="material-icons-round animate-spin">sync</span> : <>{t('finish_setup')} &amp; {t('launch_platform')}</>}
                    </button>
                )}
            </div>
        </div>
    );
};
